import { ChangeEvent, FormEvent, useState } from "react";
import {
  useListComplianceTrainingsQuery,
  useUploadComplianceTrainingDocumentMutation,
} from "../../redux/api/complianceTraining";
import { toast } from "react-toastify";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { Backup } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";

interface ComplianceTrainingRecordUploadModalProps {
  profileId: number;
  onUpload: () => void;
}

const ComplianceTrainingRecordUploadModal = ({
  profileId,
  onUpload,
}: ComplianceTrainingRecordUploadModalProps) => {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [trainingId, setTrainingId] = useState<number | undefined>(undefined);
  const [issueDate, setIssueDate] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [file, setFile] = useState<File | undefined>(undefined);
  const [noDocumentRequired, setNoDocumentRequired] = useState(false);

  const { data: trainings, isLoading, isFetching } = useListComplianceTrainingsQuery();
  const [uploadDocument] = useUploadComplianceTrainingDocumentMutation();

  const selectedTraining = trainings?.find((t) => t.id === trainingId);

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    clearForm();
  };

  const clearForm = () => {
    setTrainingId(undefined);
    setIssueDate("");
    setExpirationDate("");
    setFile(undefined);
    setNoDocumentRequired(false);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const mb = 1024 * 1024;
    if (!event.target.files || !event.target.files[0]) {
      setFile(undefined);
      return;
    }
    if (event.target.files[0].size > 20 * mb) {
      toast.error(<p>File is too big. Files must be 20MB or less.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return;
    }
    setFile(event.target.files[0]);
  };

  const toastError = (msg: string) =>
    toast.error(<p>{msg}</p>, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (trainingId === undefined) {
      toastError("Please select a training.");
      return;
    }
    if (!expirationDate) {
      toastError("Please provide an expiration date.");
      return;
    }
    const canSkipFile = noDocumentRequired && selectedTraining && !selectedTraining.documentRequired;
    if (!canSkipFile && !file) {
      toastError("No valid file to upload.");
      return;
    }

    doUpload();
  };

  const doUpload = async () => {
    setUploading(true);
    const body = JSON.stringify({
      profileIds: [profileId],
      complianceTrainingIds: [trainingId],
      issueDate: issueDate || undefined,
      expirationDate: expirationDate || undefined,
    });
    const data = new FormData();
    data.append("record", new Blob([body], { type: "application/json" }));
    if (file) data.append("file", file);
    const response = await uploadDocument(data);
    setUploading(false);
    if ("error" in response) {
      toast.error(<p>Failed to upload document. Please try again.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    } else {
      toast.success(<p>Training document uploaded successfully.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      handleClose();
      onUpload();
    }
  };

  return (
    <>
      <Button
        className="blue-link-button"
        id="compliance-training-upload"
        onClick={handleOpen}
      >
        <Backup />
        Upload Compliance Training Document
      </Button>
      <Dialog
        className="modal"
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-title"
        sx={{
          "& .MuiDialog-paper": { maxWidth: "100% !important" },
        }}
      >
        <div className="modal-header mb-2">
          <Typography id="modal-title" variant="h3">
            Upload Training Document
          </Typography>
          <Button onClick={handleClose} className="grey-link-button">
            <CloseIcon />
          </Button>
        </div>
        <form onSubmit={handleSubmit}>
          <div
            className="form-narrow"
            id="compliance-training-upload-fields"
            style={{ textOverflow: "ellipsis" }}
          >
            {!isLoading && !isFetching && (
              <div>
                <InputLabel id="trainingLabel">Compliance Training</InputLabel>
                <Select
                  labelId="trainingLabel"
                  id="training"
                  notched={false}
                  value={trainingId ?? ""}
                  label="Compliance Training"
                  onChange={(e) => {
                    setTrainingId(+e.target.value);
                    setNoDocumentRequired(false);
                    setFile(undefined);
                  }}
                >
                  {trainings
                    ?.filter((t) => !t.deleted)
                    .map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name}
                      </MenuItem>
                    ))}
                </Select>
              </div>
            )}
            <label htmlFor="issueDate">
              <span>Issue Date:</span>
              <input
                type="date"
                id="issueDate"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
              />
            </label>
            <label htmlFor="expirationDate">
              <span>Expiration Date:</span>
              <input
                type="date"
                id="expirationDate"
                value={expirationDate}
                onChange={(e) => setExpirationDate(e.target.value)}
              />
            </label>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "16px", marginTop: "3%" }}>
            <label htmlFor="fileUpload">
              <span>Upload File:</span>
              <input
                type="file"
                id="fileUpload"
                onChange={handleFileChange}
                disabled={noDocumentRequired}
              />
            </label>
            {selectedTraining && !selectedTraining.documentRequired && (
              <label
                htmlFor="noDocumentRequired"
                style={{ display: "flex", flexDirection: "column", alignItems: "center", whiteSpace: "nowrap" }}
              >
                <span>No Document Required:</span>
                <Checkbox
                  id="noDocumentRequired"
                  name="noDocumentRequired"
                  checked={noDocumentRequired}
                  onChange={(e) => {
                    setNoDocumentRequired(e.target.checked);
                    if (e.target.checked) setFile(undefined);
                  }}
                />
              </label>
            )}
          </div>
          <div className="modal-button">
            <Button className="outline-button-small" onClick={handleClose}>
              Cancel
            </Button>
            <input
              className="blue-button-small"
              type="submit"
              value="Upload Document"
              disabled={uploading}
            />
            {uploading && <CircularProgress />}
          </div>
        </form>
      </Dialog>
    </>
  );
};

export default ComplianceTrainingRecordUploadModal;
